import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  CircleAlert,
  CircleCheck,
  LoaderCircle,
  Mail,
  RefreshCw,
} from "lucide-react";
import AuthFrame from "../components/PublicChrome/AuthFrame";
import AuthorizationService from "../../sdk/services/AuthService";
import "./VerificarEmail.view.css";

type NoticeKind = "status" | "alert";

type Notice = {
  kind: NoticeKind;
  text: string;
};

type VerifyPhase = "idle" | "checking" | "confirmed" | "rejected";

const RESEND_FAILURE =
  "Não conseguimos reenviar agora. Tente de novo em alguns minutos.";

function looksLikeEmail(value: string): boolean {
  const at = value.indexOf("@");
  if (at < 1 || value.indexOf("@", at + 1) !== -1) return false;
  return value.slice(at + 1).includes(".");
}

function verifyNoticeFor(phase: VerifyPhase): Notice | null {
  if (phase === "checking") {
    return { kind: "status", text: "Verificando o link de ativação…" };
  }
  if (phase === "confirmed") {
    return { kind: "status", text: "E-mail confirmado! Você já pode entrar." };
  }
  if (phase === "rejected") {
    return { kind: "alert", text: "Link inválido ou expirado." };
  }
  return null;
}

function VerifyEmailNotice({
  notice,
  pending = false,
}: {
  notice: Notice;
  pending?: boolean;
}) {
  const alert = notice.kind === "alert";
  return (
    <div
      className={
        alert
          ? "verify-email__notice verify-email__notice--alert"
          : "verify-email__notice verify-email__notice--status"
      }
      role={alert ? "alert" : "status"}
    >
      <span className="verify-email__notice-icon">
        {pending ? (
          <LoaderCircle
            className="verify-email__submit-icon--spin"
            size={20}
            aria-hidden="true"
          />
        ) : alert ? (
          <CircleAlert size={20} aria-hidden="true" />
        ) : (
          <CircleCheck size={20} aria-hidden="true" />
        )}
      </span>
      {notice.text}
    </div>
  );
}

export default function VerificarEmailView(): JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [verifyPhase, setVerifyPhase] = useState<VerifyPhase>(
    token ? "checking" : "idle",
  );
  const calledRef = useRef(false);

  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [resendNotice, setResendNotice] = useState<Notice | null>(null);
  const sendingRef = useRef(false);

  useEffect(() => {
    if (!token || calledRef.current) return;
    calledRef.current = true;
    setVerifyPhase("checking");

    AuthorizationService.verifyEmail(token)
      .then(() => {
        setVerifyPhase("confirmed");
      })
      .catch(() => {
        setVerifyPhase("rejected");
      });
  }, [token]);

  async function handleResend(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const address = email.trim();
    if (!looksLikeEmail(address) || sendingRef.current) return;

    sendingRef.current = true;
    setSending(true);
    setResendNotice(null);

    try {
      await AuthorizationService.resendVerificationEmail(address);
      setResendNotice({
        kind: "status",
        text: `E-mail reenviado para ${address}. Confira a caixa de entrada e o spam.`,
      });
    } catch {
      setResendNotice({ kind: "alert", text: RESEND_FAILURE });
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  }

  const verifyNotice = verifyNoticeFor(verifyPhase);

  return (
    <AuthFrame screen="verificar-email">
      <div className="verify-email">
        <div className="verify-email__seal" aria-hidden="true">
          <Mail size={30} />
        </div>
        <h1 className="verify-email__title">Ative sua conta</h1>
        <p className="verify-email__lead">Enviamos um link de ativação para o e-mail que você cadastrou. Abra o e-mail e toque no link.</p>
        {verifyNotice ? (
          <VerifyEmailNotice notice={verifyNotice} pending={verifyPhase === "checking"} />
        ) : null}
        {resendNotice ? <VerifyEmailNotice notice={resendNotice} /> : null}
        <form onSubmit={handleResend} noValidate>
          <div className="verify-email__field">
            <label className="verify-email__label" htmlFor="verificar-email-address">E-mail para reenviar</label>
            <div className="verify-email__control">
              <Mail
                className="verify-email__control-icon"
                size={20}
                aria-hidden="true"
              />
              <input
                id="verificar-email-address"
                className="verify-email__input"
                name="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                placeholder="seu@email.com"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                }}
                disabled={sending}
              />
            </div>
          </div>
          <button
            type="submit"
            className="verify-email__submit"
            disabled={sending}
            aria-busy={sending || undefined}
          >
            {sending ? (
              <LoaderCircle
                className="verify-email__submit-icon verify-email__submit-icon--spin"
                size={20}
                aria-hidden="true"
              />
            ) : (
              <RefreshCw
                className="verify-email__submit-icon"
                size={20}
                aria-hidden="true"
              />
            )}
            {sending ? "Reenviando…" : "Reenviar e-mail de ativação"}
          </button>
        </form>
        <div className="verify-email__rule" aria-hidden="true" />
        <Link to="/login" className="verify-email__switch">
          <span className="verify-email__switch-prompt">Já ativou?</span>
          <span className="verify-email__switch-action">Entrar</span>
          <ArrowRight size={20} aria-hidden="true" />
        </Link>
      </div>
    </AuthFrame>
  );
}
